import type { LibraryItem } from "@/api/models/api-types";
import { getFileUrl } from "@/lib/utils";
import { extensionToMimeType } from "@/features/file-upload/utils/file-utils";
import { getFileExtension, getMediaKind, type LibraryMediaKind } from "@/features/library/lib/library-media";
import type { PickerConfig, SelectedFile } from "../types/file-picker-types";

export type PickCompatibility =
  | { status: "ok" }
  /** Görsel istenen alanda video: kare seçilerek kullanılabilir */
  | { status: "frame" }
  | { status: "blocked"; reasonKey: string };

const DOCUMENT_HINTS = ["pdf", "word", "document", "msword", "text/", "sheet", "excel", "presentation"];

const kindOfMime = (mime: string): LibraryMediaKind | null => {
  const lower = mime.toLowerCase();
  if (lower.startsWith("image/")) return "image";
  if (lower.startsWith("video/")) return "video";
  if (lower.startsWith("audio/")) return "audio";
  if (DOCUMENT_HINTS.some((hint) => lower.includes(hint))) return "document";
  return null;
};

/** Config'teki kısıtları MIME desenlerine çevirir; kısıt yoksa boş döner. */
const getAllowedPatterns = (config: PickerConfig | null | undefined) => {
  const types = config?.allowedTypes ?? [];
  if (types.length > 0) return types;
  return (config?.allowedExtensions ?? []).map((ext) => extensionToMimeType(ext));
};

const matchesPattern = (mime: string, kind: LibraryMediaKind | null, pattern: string) => {
  if (pattern === "*" || pattern === "*/*") return true;
  if (pattern.endsWith("/*")) {
    const base = pattern.slice(0, -2).toLowerCase();
    return mime.toLowerCase().startsWith(`${base}/`) || kind === base;
  }
  return mime.toLowerCase() === pattern.toLowerCase();
};

/** MIME tipi (ve gerekirse türü) config kısıtlarına uyuyor mu? */
export const isMimeAccepted = (
  mime: string,
  config: PickerConfig | null | undefined,
  kind: LibraryMediaKind | null = kindOfMime(mime)
) => {
  const patterns = getAllowedPatterns(config);
  if (patterns.length === 0) return true;
  return patterns.some((pattern) => matchesPattern(mime, kind, pattern));
};

/** Config'in kabul ettiği medya türleri; kısıt yoksa null. */
export const getAcceptedKinds = (config: PickerConfig | null | undefined) => {
  const patterns = getAllowedPatterns(config);
  if (patterns.length === 0 || patterns.some((p) => p === "*" || p === "*/*")) return null;
  const kinds = new Set<LibraryMediaKind>();
  for (const pattern of patterns) {
    const kind = pattern.endsWith("/*")
      ? (pattern.slice(0, -2) as LibraryMediaKind)
      : kindOfMime(pattern);
    if (kind) kinds.add(kind);
  }
  return kinds;
};

const getBlockedReasonKey = (config: PickerConfig | null | undefined) => {
  const kinds = getAcceptedKinds(config);
  if (kinds && kinds.size === 1) {
    const [only] = [...kinds];
    return `filePicker.incompatible.only.${only}`;
  }
  return "filePicker.incompatible.generic";
};

export const getPickCompatibility = (
  item: LibraryItem,
  config: PickerConfig | null | undefined
): PickCompatibility => {
  const kind = getMediaKind(item);
  const mime = item.file?.type || "";
  const patterns = getAllowedPatterns(config);
  if (patterns.length === 0) return { status: "ok" };

  const byMime = mime && isMimeAccepted(mime, config, kind);
  const byExtension =
    !mime && (config?.allowedExtensions ?? []).some(
      (ext) => ext.replace(/^\./, "").toLowerCase() === getFileExtension(item).toLowerCase()
    );
  if (byMime || byExtension) return { status: "ok" };

  if (kind === "video" && isMimeAccepted("image/png", config, "image")) {
    return { status: "frame" };
  }
  return { status: "blocked", reasonKey: getBlockedReasonKey(config) };
};

/** Yerel dosya (bırakma, yapıştırma) config'e uyuyor mu? */
export const isLocalFileAccepted = (file: File, config: PickerConfig | null | undefined) => {
  if (file.type) return isMimeAccepted(file.type, config);
  const extension = file.name.split(".").pop() ?? "";
  return isMimeAccepted(extensionToMimeType(extension), config);
};

export { getBlockedReasonKey };

/** Library öğesini seçicinin dışa verdiği biçime çevirir. */
export const toSelectedFile = (item: LibraryItem): SelectedFile => {
  const fileNameWithExt = item.file.fileUrl.split("/").pop() || "";
  const extension = fileNameWithExt.match(/\.[^.]+$/)?.[0] ?? "";
  let name = item.title || fileNameWithExt || "Untitled File";
  if (item.title && extension && !item.title.endsWith(extension)) {
    name = `${item.title}${extension}`;
  }

  return {
    id: item._id,
    name,
    size: item.file.size,
    type: item.file.type,
    source: "library",
    url: item.file.fileUrl,
    thumbnail: getMediaKind(item) === "image" ? getFileUrl(item.file) || undefined : undefined,
    createdAt: item.createdAt,
    categories: item.categories,
    tags: item.tags,
  };
};
